import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { BookOpen, Video, HardDrive } from 'lucide-react';
import Pagination from '../../components/ui/Pagination';
import { trainerService } from '../../services';

/**
 * Workbooks Page - Trainer
 * View all workbooks from assigned sessions (read-only)
 */

const WorkbooksPage = () => {
  const navigate = useNavigate();
  const [workbooks, setWorkbooks] = useState([]);
  const [filteredWorkbooks, setFilteredWorkbooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSession, setSelectedSession] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchWorkbooks();
  }, []);

  const fetchWorkbooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trainerService.getWorkbooks();
      if (response.success) {
        const allWorkbooks = response.data || [];
        setWorkbooks(allWorkbooks);
        setFilteredWorkbooks(allWorkbooks);
      } else {
        setError(response.error || 'Failed to load workbooks');
      }
    } catch (error) {
      console.error('Error fetching workbooks:', error);
      setError(error.message || 'An error occurred while loading workbooks');
    } finally {
      setLoading(false);
    }
  };

  // Filter workbooks
  useEffect(() => {
    let filtered = workbooks;

    // Filter by session
    if (selectedSession !== 'all') {
      filtered = filtered.filter((wb) => wb.sessionId === selectedSession || wb.session?._id === selectedSession);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (wb) =>
          (wb.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (wb.sessionName || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredWorkbooks(filtered);
    setCurrentPage(1);
  }, [searchQuery, selectedSession, workbooks]);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get unique sessions for filter
  const uniqueSessions = workbooks.reduce((acc, wb) => {
    const sessionId = wb.sessionId || wb.session?._id;
    const sessionName = wb.sessionName || wb.session?.name;
    if (sessionId && !acc.find(s => s.id === sessionId)) {
      acc.push({ id: sessionId, name: sessionName });
    }
    return acc;
  }, []);

  // Pagination
  const totalPages = Math.ceil(filteredWorkbooks.length / itemsPerPage);

  return (
    <DashboardLayout title="Workbooks">
      <div className="p-4">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group mb-4"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Workbooks</h1>
          <p className="text-sm text-neutral-600 mt-1">View all workbooks from your assigned sessions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <StatCard label="Total Workbooks" value={workbooks.length}        icon={BookOpen}  variant="blue" />
          <StatCard label="Sessions"        value={uniqueSessions.length}    icon={Video}     variant="violet" />
          <StatCard label="Total Size"      value={`${(workbooks.reduce((acc, wb) => acc + (wb.size || 0), 0) / 1024 / 1024).toFixed(0)} MB`} icon={HardDrive} variant="emerald" />
        </div>

        {/* Workbooks Card */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          {/* Search and Filters inside card */}
          <div className="p-5 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search workbooks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-neutral-50/50 placeholder:text-neutral-400"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  className="px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700 min-w-[140px]"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Sessions</option>
                  {uniqueSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.name}
                    </option>
                  ))}
                </select>
                <span className="text-xs font-medium text-neutral-400 px-2.5 py-1 bg-neutral-50 rounded-full">{filteredWorkbooks.length} files</span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-neutral-500 mt-4 text-sm">Loading workbooks...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-4">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-neutral-900 mb-1">Error loading workbooks</p>
                <p className="text-xs text-neutral-400 mb-4">{error}</p>
                <Button variant="primary" size="sm" onClick={fetchWorkbooks}>
                  Try Again
                </Button>
              </div>
            ) : filteredWorkbooks.length > 0 ? (
              <table className="w-full table-fixed min-w-[640px]">
                <thead className="hidden sm:table-header-group">
                  <tr className="border-b border-neutral-100">
                    <th className="text-left py-3 px-4 text-xs font-medium text-neutral-500 w-[30%]">File</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-neutral-500 w-[30%]">Session</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-neutral-500 w-[10%]">Size</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-neutral-500 w-[12%]">Date</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-neutral-500 w-[18%]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWorkbooks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((workbook, index) => {
                    const ext = (workbook.name || '').split('.').pop()?.toLowerCase() || '';
                    const typeConfig = {
                      pdf: { color: 'bg-red-50 text-red-600', label: 'PDF' },
                      doc: { color: 'bg-blue-50 text-blue-600', label: 'DOC' },
                      docx: { color: 'bg-blue-50 text-blue-600', label: 'DOCX' },
                      ppt: { color: 'bg-orange-50 text-orange-600', label: 'PPT' },
                      pptx: { color: 'bg-orange-50 text-orange-600', label: 'PPTX' },
                      xls: { color: 'bg-green-50 text-green-600', label: 'XLS' },
                      xlsx: { color: 'bg-green-50 text-green-600', label: 'XLSX' },
                    };
                    const fileType = typeConfig[ext] || { color: 'bg-primary-50 text-primary-600', label: ext.toUpperCase() || 'FILE' };

                    return (
                      <tr
                        key={workbook.id || workbook.url || `workbook-${index}`}
                        className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors cursor-pointer block sm:table-row mb-3 sm:mb-0 p-3 sm:p-0 rounded-lg sm:rounded-none"
                        onClick={() => {
                          if (workbook.url && workbook.url.startsWith('http')) {
                            const fileExt = workbook.url.split('.').pop()?.toLowerCase() || '';
                            if (['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(fileExt)) {
                              window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(workbook.url)}&embedded=true`, '_blank');
                            } else {
                              window.open(workbook.url, '_blank');
                            }
                          }
                        }}
                      >
                        <td className="py-3 px-4 block sm:table-cell">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 ${fileType.color} rounded-full flex items-center justify-center font-semibold text-[10px] flex-shrink-0`}>
                              {fileType.label}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm font-medium text-neutral-900 truncate">{workbook.name || 'Untitled Workbook'}</p>
                              <p className="text-xs text-neutral-500 truncate sm:hidden">{workbook.sessionName || 'Untitled Session'}</p>
                              {workbook.hasValidUrl === false && (
                                <span className="text-xs text-red-500">URL Missing</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 sm:hidden text-xs text-neutral-500">
                              <span>{formatFileSize(workbook.size)}</span>
                              <span>{formatDate(workbook.uploadedAt || workbook.sessionDate)}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3 hidden sm:table-cell">
                          <div className="min-w-0">
                            <p className="text-sm text-neutral-700 truncate">{workbook.sessionName || 'Untitled Session'}</p>
                          </div>
                        </td>
                        <td className="py-3 px-3 hidden sm:table-cell">
                          <span className="text-xs text-neutral-500">{formatFileSize(workbook.size)}</span>
                        </td>
                        <td className="py-3 px-3 hidden sm:table-cell">
                          <span className="text-xs text-neutral-500 whitespace-nowrap">{formatDate(workbook.uploadedAt || workbook.sessionDate)}</span>
                        </td>
                        <td className="py-3 px-4 text-right hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-0">
                            <button
                              disabled={workbook.hasValidUrl === false}
                              onClick={() => {
                                if (workbook.url && workbook.url.startsWith('http')) {
                                  const fileExt = workbook.url.split('.').pop()?.toLowerCase() || '';
                                  if (['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(fileExt)) {
                                    window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(workbook.url)}&embedded=true`, '_blank');
                                  } else {
                                    window.open(workbook.url, '_blank');
                                  }
                                }
                              }}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title="View Workbook"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => navigate(`/trainer/sessions/${workbook.sessionId || workbook.session?._id}`)}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                              title="View Session"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                            <button
                              disabled={workbook.hasValidUrl === false}
                              onClick={async () => {
                                if (workbook.url && workbook.url.startsWith('http')) {
                                  try {
                                    const response = await fetch(workbook.url);
                                    const blob = await response.blob();
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = workbook.name || 'workbook';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(blobUrl);
                                  } catch {
                                    window.open(workbook.url, '_blank');
                                  }
                                }
                              }}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Download"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <p className="text-sm text-neutral-600 font-medium mb-1">No workbooks found</p>
                <p className="text-xs text-neutral-400">
                  {searchQuery || selectedSession !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Workbooks will appear here once clients upload them to your sessions'}
                </p>
              </div>
            )}
          </div>
          {/* Pagination */}
          {filteredWorkbooks.length > 0 && (
            <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredWorkbooks.length}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WorkbooksPage;
